import { useContext, useMemo } from 'react';

import { useAppSelector } from '@/hooks';
import { selectLookup } from '@/store/slices';

import { config } from '../config';
import { FormContext } from '../contexts';
import { useFormValues } from '../core';
import type { FormInitialValues, IFormSelect, SelectOption } from '../typings';
import { indexAddressGroups } from '../utils/addressLink';
import { describeAddressTarget } from '../utils/addressSummary';
import { GONE_SLOT_LABEL, selectValues } from '../utils/practiceSlots';

/**
 * The select's options: each one whose lookup entry names an address entry also names that
 * entry's place, and one naming no live entry is left out unless it is chosen.
 */
export const useLookupEntryAddressOptions = (
  field: IFormSelect,
  fieldName: string,
): SelectOption[] | undefined => {
  const { formFieldGroups } = useContext(FormContext);
  const values = useFormValues<FormInitialValues>();
  const lookups = useAppSelector(selectLookup);
  const { options, lookupEntryAddresses } = field;

  const index = useMemo(
    () => indexAddressGroups(formFieldGroups),
    [formFieldGroups],
  );

  // One string of [option value, description] pairs, so the options rebuild only when a
  // description changes; null stands for a slot no live entry holds.
  const descriptions = JSON.stringify(
    (lookupEntryAddresses ?? [])
      .filter(slot => slot.optionValue !== undefined)
      .map(({ optionValue, targetFieldId }) => {
        if (!targetFieldId) return [optionValue, null];

        const { name, address } = describeAddressTarget(
          targetFieldId,
          index,
          values,
          lookups,
        );
        return [
          optionValue,
          [name, address].filter(part => part !== '').join(', '),
        ];
      }),
  );

  // Joined, so the options rebuild only when the choice changes.
  const chosen = selectValues(values[fieldName]).join(
    config.multiValueDivider,
  );

  return useMemo(() => {
    if (!options || !lookupEntryAddresses?.length) return options;

    const byValue = new Map(
      JSON.parse(descriptions) as [string, string | null][],
    );
    const chosenValues = new Set(selectValues(chosen));

    return options.flatMap(option => {
      const value = String(option.value);
      if (!byValue.has(value)) return [option];

      const description = byValue.get(value);
      if (description === null) {
        // Chosen before its practice went, it stays, flagged, so the provider can see and remove it.
        return chosenValues.has(value)
          ? [{ ...option, label: `${option.label} — ${GONE_SLOT_LABEL}` }]
          : [];
      }

      return [
        description ? { ...option, label: `${option.label} — ${description}` } : option,
      ];
    });
  }, [options, lookupEntryAddresses, descriptions, chosen]);
};
